<script setup lang="ts">
import { getRelicName, relicCollection } from '@/core/relic'
import { useUserStore } from '@/stores/modules/user'
import type { RelicItemType } from '@/types/relic'
import { equipQualityText, noPercentEquipAttrKeyList } from '../data'
import { BasicAttrTextEnum, EquipPriceEnum } from '@/enums'
import { computed } from 'vue'
import { formatNumber } from '@/utils'
import { socketEmitAsync } from '@/utils/web-socket'
import type { RelicAttrType, UserAttrType } from '@/types/user'
import { Lock, Unlock } from '@element-plus/icons-vue'
import { cloneDeep } from 'lodash'
import { relicIcon, relicElementNameMap } from '../data'

const userStore = useUserStore()

const props = withDefaults(
  defineProps<{
    relicInfo: RelicItemType
    isInItems?: boolean
    isInComparison?: boolean
    showAction?: boolean
    showLock?: boolean
    showGrow?: boolean
  }>(),
  {
    isInItems: true,
    isInComparison: false,
    showAction: true,
    showLock: true,
    showGrow: false,
  },
)
const emits = defineEmits(['clear', 'lock', 'compare', 'closeCompare'])

const relicCollectionInfo = computed(() => {
  const { relicId } = props.relicInfo
  return relicCollection[relicId]
})
const repicBasicAttr = computed(() => {
  if (!props.relicInfo) return {}
  let { count } = props.relicInfo
  if (count > 50) count = 50

  if (!relicCollectionInfo.value) return {}
  const res = cloneDeep(relicCollectionInfo.value.attrs.basic)
  const grow = relicCollectionInfo.value.grow.basic
  let key: keyof RelicAttrType
  for (key in grow) {
    const growValue = (grow[key] || 0) * count
    if (!growValue) continue

    if (!res[key] && grow[key]) {
      res[key] = growValue
    } else {
      if (!grow[key]) continue
      res[key]! += growValue
    }
  }
  if (userStore.soulType > 3) {
    for (key in res) {
      if (['windDa', 'fireDa', 'waterDa', 'soilDa'].includes(key)) {
        if (key === 'windDa') res.windIndDa = res.windDa
        else if (key === 'fireDa') res.fireIndDa = res.fireDa
        else if (key === 'waterDa') res.waterIndDa = res.waterDa
        else if (key === 'soilDa') res.soilIndDa = res.soilDa
        delete res[key]
      }
    }
  }

  return res
})

async function handleEquipRelic(action: 'wear' | 'remove', index?: number) {
  if (userStore.getItemEquipTotal >= userStore.getItemCapacity) return ElMessage.warning('背包已满')
  const id = props.relicInfo.id
  const res = await socketEmitAsync<UserAttrType>({ type: 'relicAction', data: { id, action, index } })
  if (action === 'wear') {
    userStore.relicEquippedList[index!] = id
  } else {
    const index = userStore.relicEquippedList.findIndex((item) => item === id)
    userStore.relicEquippedList[index!] = ''
  }
  userStore.userAttr = res
  userStore.setRoutineEquippedList()
  emits('clear')
  if (props.isInComparison) emits('closeCompare')
  ElMessage.success(action === 'wear' ? '携带成功' : '卸下成功')
}
async function handleLock() {
  const id = props.relicInfo.id
  await socketEmitAsync<RelicItemType[]>({ type: 'lockRelic', data: { id } })
  const relic = userStore.relicList.find((item) => item.id === id)
  if (!relic) return
  relic.isLock = !relic.isLock
  emits('lock')
}

const relicEquippedList = computed(() =>
  userStore.relicEquippedList.map((item) => {
    const relic = userStore.relicList.find((relic) => relic.id === item)
    if (!relic) return undefined
    return relic
  }),
)
</script>

<template>
  <div class="relic-info affix">
    <p :class="relicInfo.quality" style="position: relative; margin-bottom: 8px; font-weight: bold">
      {{ getRelicName(relicInfo.relicId, relicInfo.count) }}
      <span class="relic-lock" v-show="showAction && showLock">
        <el-button size="small" :type="relicInfo.isLock ? 'success' : 'info'" plain @click="handleLock">
          <el-icon size="20" v-show="!relicInfo.isLock"><Unlock /></el-icon>
          <el-icon size="20" v-show="relicInfo.isLock"><Lock /></el-icon>
        </el-button>
      </span>
    </p>
    <p>
      品质：<span :class="relicInfo.quality">{{ equipQualityText[relicInfo?.quality || 'normal'] }}</span>
    </p>
    <p v-show="relicInfo?.count">
      数量：<span class="grow">{{ relicInfo?.count }}</span>
    </p>
    <p>属性：</p>
    <p v-for="(val, key) of repicBasicAttr" :key="key">
      <template v-if="val && val > 0">
        {{ BasicAttrTextEnum[key] }}
        <span>
          +
          <span class="grow">{{ formatNumber(val, 2) }}{{ noPercentEquipAttrKeyList.includes(key) ? '' : '%' }}</span>
        </span>
      </template>
    </p>
    <template v-if="relicCollectionInfo.changeAtkElement">
      <br />
      <p>特殊属性：</p>
      <p class="special">
        改变攻击元素为【{{ relicIcon[relicCollectionInfo.changeAtkElement] }}{{ relicElementNameMap[relicCollectionInfo.changeAtkElement] }}】
      </p>
    </template>
    <template v-if="showGrow">
      <br />
      <p>成长属性：</p>
      <p v-for="(val, key) of relicCollectionInfo.grow.basic" :key="key">
        <template v-if="val && val > 0">
          {{ BasicAttrTextEnum[key] }}
          <span>
            +
            <span class="grow"
              >{{ val }}{{ ['crt', 'crtd', 'hr', 'atksp', 'ad', 'windDa', 'fireDa', 'waterDa', 'soilDa'].includes(key) ? '%' : '' }}</span
            >
          </span>
        </template>
      </p>
    </template>
  </div>
  <el-row class="equip-action" v-show="isInItems && showAction">
    <el-col :span="8">
      <el-popover placement="top-start" :width="150" trigger="click" :teleported="false">
        <template #reference>
          <CommonBtn type="success" size="small">装备</CommonBtn>
        </template>
        <div class="hour-list affix">
          <el-button
            v-for="(item, index) of relicEquippedList"
            :type="item ? 'primary' : 'info'"
            size="small"
            plain
            @click="handleEquipRelic('wear', index)"
          >
            <span v-if="item" :class="item.quality">{{ getRelicName(item.relicId, item.count) }}</span>
            <span v-else>（未设置）</span>
          </el-button>
        </div>
      </el-popover>
    </el-col>
    <el-col :span="8" v-if="!isInComparison">
      <el-popover placement="top-start" :width="150" trigger="click" :teleported="false">
        <template #reference>
          <CommonBtn type="primary" size="small">比对</CommonBtn>
        </template>
        <div class="hour-list affix">
          <el-button v-for="(item, index) of relicEquippedList" :type="item ? 'primary' : 'info'" size="small" plain @click="emits('compare', index)">
            <span v-if="item" :class="item.quality">{{ getRelicName(item.relicId, item.count) }}</span>
            <span v-else>（未设置）</span>
          </el-button>
        </div>
      </el-popover>
    </el-col>
  </el-row>
  <el-row class="equip-action" v-show="!isInItems && showAction">
    <el-col :span="8">
      <CommonBtn type="danger" size="small" @done="handleEquipRelic('remove')">卸下</CommonBtn>
    </el-col>
  </el-row>
</template>

<style lang="scss" scoped>
.relic-info {
  font-size: 14px;
  height: 100%;
  overflow: auto;
  margin-bottom: 10px;
  p {
    margin: 4px 0;
  }
  .relic-lock {
    position: absolute;
    right: 4px;
    top: calc(100% + 10px);
  }
}
:deep(.hour-list) {
  .el-button:focus,
  .el-button:hover {
    background-color: unset;
  }
}
</style>
